import React, { useMemo, useState } from "react";
import { getPreserveExpectedErrorsForCallerContext } from "../../../../../utils/apolloErrorPolicy";
import { TextField } from "@mui/material";
import SearchableSelectField from "../../../../common/dashboard/SearchableSelectField";
import {
  useArchiveCmpEventTypeMutation,
  useCreateCmpEventTypeMutation,
  useGetCmpEventTypesQuery,
  useUpdateCmpEventTypeMutation,
} from "../../../../../graphql/types";
import ColorPicker from "../../../../common/FormComponents/ColorPicker";
import { cpaGold } from "../../EventSales/eventSalesShared";
import { FieldLabel, WizardActionButton, wizardTextFieldSx } from "./wizardShared";
import {
  eventLibraryFieldClassName,
  DeleteConfirmModal,
  LibraryEditDialog,
  SuccessPopup,
  deletedMessage,
} from "./EventLibrarySelect";
import { buildStaffErrorFallback, getUserFacingErrorText } from "../../../../../utils/userFacingError";

type EventTypeSelectProps = {
  chamberId: string;
  value: string;
  currentLabel?: string;
  currentColor?: string;
  onChange: (selection: { id: string; label: string; color: string }) => void;
  error?: string;
  /** Community/Member events keep the field in place, greyed out (design). */
  disabled?: boolean;
};

// The Add Event Type mock opens with the purple swatch the color picker shows
// as HEX #9751F2 (event type popups sheet).
const DEFAULT_COLOR = "#9751F2";
const catalogueErrorContext = getPreserveExpectedErrorsForCallerContext({ expectedGraphQLErrorCodes: ["BAD_USER_INPUT"] });

const EventTypeSelect = ({
  chamberId,
  value,
  currentLabel,
  currentColor,
  onChange,
  error,
  disabled = false,
}: EventTypeSelectProps): React.ReactElement => {
  const { data, refetch } = useGetCmpEventTypesQuery({
    variables: { input: { chamberId, includeArchived: false } },
    skip: !chamberId,
    fetchPolicy: "cache-and-network",
    context: catalogueErrorContext,
  });
  const eventTypes = useMemo(
    () => data?.getCmpEventTypes.eventTypes || [],
    [data]
  );
  const selected = eventTypes.find((eventType) => eventType.id === value);
  const options = eventTypes.map((eventType) => ({ value: eventType.id, label: eventType.label, color: eventType.color }));
  if (value && !selected && currentLabel) {
    options.push({ value, label: currentLabel, color: currentColor || cpaGold });
  }

  const [createType, { loading: creating }] = useCreateCmpEventTypeMutation({ context: catalogueErrorContext });
  const [updateType, { loading: updating }] = useUpdateCmpEventTypeMutation({ context: catalogueErrorContext });
  const [archiveType, { loading: archiving }] = useArchiveCmpEventTypeMutation({ context: catalogueErrorContext });

  const [dialog, setDialog] = useState<"add" | "edit" | null>(null);
  const [draftLabel, setDraftLabel] = useState("");
  const [draftColor, setDraftColor] = useState(DEFAULT_COLOR);
  const [confirmDelete, setConfirmDelete] = useState(false);
  const [failure, setFailure] = useState("");
  const [result, setResult] = useState<{ message: string; highlights: string[] } | null>(null);
  // Edit or Delete with nothing selected (Create Event Screen.dc.html:1826-1829).
  const [selectionError, setSelectionError] = useState("");
  const requireSelection = () => {
    if (value) return true;
    setSelectionError("Select an Event Type first.");
    return false;
  };

  const openAdd = () => {
    setFailure("");
    setSelectionError("");
    setDraftLabel("");
    setDraftColor(DEFAULT_COLOR);
    setDialog("add");
  };
  const openEdit = () => {
    if (!requireSelection()) {
      return;
    }
    setSelectionError("");
    if (!selected) {
      return;
    }
    setFailure("");
    setDraftLabel(selected.label);
    setDraftColor(selected.color);
    setDialog("edit");
  };

  const handleSave = async () => {
    if (!draftLabel.trim()) {
      return;
    }
    setFailure("");
    try {
      if (dialog === "add") {
        const created = await createType({
          variables: {
            input: { chamberId, label: draftLabel.trim(), color: draftColor },
          },
        });
        await refetch();
        const item = created.data?.createCmpEventType?.eventType;
        if (!item) throw new Error("The event type could not be saved.");
        onChange({ id: item.id, label: item.label, color: item.color });
      } else if (dialog === "edit" && selected) {
        const updated = await updateType({
          variables: {
            input: {
              chamberId,
              eventTypeId: selected.id,
              label: draftLabel.trim(),
              color: draftColor,
            },
          },
        });
        await refetch();
        const item = updated.data?.updateCmpEventType?.eventType;
        if (!item) throw new Error("The event type could not be saved.");
        onChange({ id: item.id, label: item.label, color: item.color });
      }
      setDialog(null);
    } catch (cause) {
      setFailure(getUserFacingErrorText(cause, buildStaffErrorFallback("save the Event Type")));
    }
  };

  const handleDelete = async () => {
    if (!selected) {
      return;
    }
    setFailure("");
    try {
      const result = await archiveType({
        variables: { input: { chamberId, eventTypeId: selected.id } },
      });
      if (!result.data?.archiveCmpEventType.eventTypeSavedSuccessfully) throw new Error("The event type could not be deleted.");
      await refetch();
      onChange({ id: "", label: "", color: cpaGold });
      setConfirmDelete(false);
      setResult(deletedMessage("Event Type", selected.label));
    } catch (cause) {
      setConfirmDelete(false);
      setFailure(getUserFacingErrorText(cause, buildStaffErrorFallback("delete the Event Type")));
    }
  };

  return (
    <div className={eventLibraryFieldClassName}>
      {/* Claude Design (8) Create Event Screen.dc.html:125-133. */}
      <FieldLabel className="contents [&>span]:leading-[normal]" hintClassName="text-[13px] leading-[normal] text-[#0A0A0A]" required hint="Add a new event type and colour to display on your calendar, or select an existing one, to easily filter events.">
        Event Type
      </FieldLabel>
      <SearchableSelectField
        id="event-type"
        invalid={Boolean(error)}
        options={options}
        placeholder="Select Event Type"
        searchPlaceholder="Search Event Type"
        value={value}
        onChange={(nextValue) => {
          const next = options.find((option) => option.value === nextValue);
          setSelectionError("");
          onChange({
            id: nextValue,
            label: next?.label || "",
            color: next?.color || cpaGold,
          });
        }}
        disabled={disabled}
      />
      <div
        aria-disabled={disabled || undefined}
        className={`flex flex-wrap items-center gap-[8px] [&_.MuiButton-root]:!min-h-[26px] [&_.MuiButton-root]:!px-[10px] ${disabled ? "pointer-events-none opacity-50" : ""}`}
      >
        <WizardActionButton tone="black" size="small" onClick={openAdd}>
          Add New Event Type
        </WizardActionButton>
        <WizardActionButton
          tone="gold"
          size="small"
          onClick={openEdit}
        >
          Edit
        </WizardActionButton>
        <WizardActionButton
          tone="red"
          size="small"
          onClick={() => { setFailure(""); if (!requireSelection()) return; setSelectionError(""); if (selected) setConfirmDelete(true); }}
        >
          Delete
        </WizardActionButton>
        {/* Inline beside the buttons, not under the dropdown (design :132-133). */}
        {error ? <span className="text-[12px] text-[#F0202E]">This field is required</span> : null}
        {selectionError && !value ? <span className="text-[12px] text-[#F0202E]">{selectionError}</span> : null}
        {failure && !dialog && <p role="alert" className="w-full text-sm text-red-600">{failure}</p>}
      </div>

      <LibraryEditDialog
        open={Boolean(dialog)}
        title={dialog === "edit" ? "Edit Event Type" : "Add Event Type"}
        confirmLabel={dialog === "edit" ? "Edit Event Type" : "Save Event Type"}
        confirmDisabled={creating || updating || !draftLabel.trim()}
        onConfirm={handleSave}
        onClose={() => setDialog(null)}
      >
        {/* "Add Event Type" (Create Event Screen.dc.html:2293). */}
        <TextField
          fullWidth
          size="small"
          placeholder="Add Event Type"
          inputProps={{ maxLength: 120 }}
          value={draftLabel}
          onChange={({ target }) => setDraftLabel(target.value)}
          sx={wizardTextFieldSx}
        />
        <div className="mt-6">
          <FieldLabel required hint="This colour will represent the event type across your site.">
            Select Event Type Colour
          </FieldLabel>
          <div className="w-fit rounded-[9px] border border-[#ECECEC] bg-white p-[8px] shadow-sm [&_button]:h-[26px] [&_button]:w-[26px] [&_button]:rounded-[7px] [&_button]:border-0 [&_button]:shadow-none">
            <ColorPicker value={draftColor} onChange={setDraftColor} />
          </div>
        </div>
        {failure && <p role="alert" className="mt-3 text-sm text-red-600">{failure}</p>}
      </LibraryEditDialog>

      {/* The mock's confirm popup reads "Delete Event Title"; the quirk policy
          corrects the heading to the event-type noun. */}
      <DeleteConfirmModal
        open={confirmDelete}
        heading="Delete Event Type"
        entity
        name={selected?.label || ""}
        loading={archiving}
        onConfirm={handleDelete}
        onCancel={() => setConfirmDelete(false)}
      />

      <SuccessPopup
        open={Boolean(result)}
        title={result?.message || ""}
        highlights={result?.highlights}
        onClose={() => setResult(null)}
      />
    </div>
  );
};

export default EventTypeSelect;
